import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentProps,
  forwardRef,
  HTMLAttributes,
  LabelHTMLAttributes,
  useMemo,
} from 'react';
import {mergeRefs} from 'react-merge-refs';
import styled from 'styled-components';

import Label from '../Label';

interface Props
  extends Omit<
      HTMLAttributes<HTMLElement> &
        ButtonHTMLAttributes<HTMLElement> &
        LabelHTMLAttributes<HTMLElement> &
        Omit<AnchorHTMLAttributes<HTMLElement>, 'type'>,
      'children'
    >,
    Pick<ComponentProps<typeof Label>, 'children'> {
  label?: boolean;
}

const UnstyledButton = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  display: inline-block;
  border-radius: 0;
`;

const UnstyledA = styled.a`
  display: inline-block;
  text-decoration: inherit;
  color: inherit;
  font-weight: inherit;
`;

const InlineDiv = styled.div`
  display: inline-block;
`;

const InlineLabel = styled(Label)`
  display: inline-block;
`;

/**
 * A utility component meant to be used to allow a generic component to be a
 * label, button, or div as appropriate
 *
 * It will be a label if `label` or `htmlFor` is specified, a button if
 * `onClick` or `type` is specified, and a div otherwise
 */
export default forwardRef<HTMLElement, Props>(
  (
    {
      className,
      children,
      onClick,
      disabled,
      label,
      htmlFor,
      type,
      href,
      ...forward
    }: Props,
    ref
  ) => {
    const contraRef = useMemo(() => mergeRefs([ref]), [ref]);

    if (label || htmlFor || typeof children === 'function') {
      return (
        <InlineLabel
          {...forward}
          ref={contraRef}
          className={className}
          htmlFor={htmlFor}
          onClick={onClick}
        >
          {children}
        </InlineLabel>
      );
    }
    if (href && !disabled) {
      return (
        <UnstyledA
          {...forward}
          ref={contraRef}
          className={className}
          href={href}
          onClick={onClick}
        >
          {children}
        </UnstyledA>
      );
    }
    if (onClick || type || disabled) {
      return (
        <UnstyledButton
          {...forward}
          ref={contraRef}
          className={className}
          onClick={onClick}
          disabled={disabled}
          type={type || 'button'}
        >
          {children}
        </UnstyledButton>
      );
    }
    return (
      <InlineDiv {...forward} ref={contraRef} className={className}>
        {children}
      </InlineDiv>
    );
  }
);
